<template>
  <AccessHandler
    :requiredRoleGroups="roleGroups"
    @accessChanged="(a: boolean) => (hasAccess = a)"
  />
  <div class="inspections-submenu-page" data-cy="inspections-submenu-page">
    <MasterPage :subApplication="SubApplicationTypes.ALL_FEATURES">
      <template v-if="hasAccess" v-slot:content>
        <header class="page__header--container">
          <h1 class="page__title--text">
            <span>{{ isRc ? "RC MENU" : "RFR MENU" }}</span>
            <small v-if="isRc">REVIEW SUBMITTED INSPECTION REPORTS</small>
            <small v-else
              >Complete your Inspection and/or Financial Reports
              <br /><br /><span style="color: black"
                >Complete all Inspection Reports before submitting any
                associated Electronic Financial Reports</span
              ></small
            >
          </h1>
        </header>
        <div class="inspections-submenu-page__content-container">
          <span class="inspections-submenu-page__notice"></span>
          <div class="inspections-submenu-page__content">
            <div
              class="inspections-submenu-page__card"
              @click="redirect(`${Pages.RfrVisitsSearch}?type=Inspection&menu=${isRc ? 'VisitsAsRc' : 'VisitsAsRfr'}`)"
            >
              <span class="inspections-submenu-page__card-title"
                >Manage your EIR Electronic Inspection Reports</span
              >
              <span
                v-if="isRc"
                class="inspections-submenu-page__card-description"
                >Review Submitted Reports <br />
                List of Open Visits and Paper Submission</span
              >
              <span v-else class="inspections-submenu-page__card-description"
                >Submit inspection <br />
                EIR Report(s)</span
              >
            </div>
            <div
              v-if="isRc"
              class="inspections-submenu-page__card"
              @click="redirect(`${Pages.RfrVisitsSearch}?type=Overdue&menu=VisitsAsRc`)"
            >
              <span class="inspections-submenu-page__card-title"
                >Manage your overdue Inspections</span
              >
              <span class="inspections-submenu-page__card-description"
                >EIRs Submitted and Not Yet Closed<br />
                (older than 2 weeks)</span
              >
            </div>
            <div
              v-if="!isRc"
              class="inspections-submenu-page__card"
              @click="redirect(`${Pages.RfrVisitsSearch}?type=Financial&menu=${isRc ? 'VisitsAsRc' : 'VisitsAsRfr'}`)"
            >
              <span class="inspections-submenu-page__card-title"
                >Manage your EFR Electronic Financial Reports</span
              >
              <span class="inspections-submenu-page__card-description"
                >Submit expenses for previously<br />
                Submitted Inspection Reports</span
              >
            </div>
            <div
              class="inspections-submenu-page__card"
              @click="redirect(`${Pages.RfrVisitsSearch}?type=Historical&menu=${isRc ? 'VisitsAsRc' : 'VisitsAsRfr'}`)"
            >
              <span class="inspections-submenu-page__card-title"
                >Electronic Inspection Report History</span
              >
              <span
                v-if="isRc"
                class="inspections-submenu-page__card-description"
                >Closed Reports<br />& Open Reports (90 days past the assignment
                End Date)</span
              >
              <span v-else class="inspections-submenu-page__card-description"
                >Review Submitted EIR Reports<br />List of Open Visits and Paper
                Submissions</span
              >
            </div>
            <div
              v-if="!isRc"
              class="inspections-submenu-page__card"
              @click="redirect(`${Pages.RfrVisitsSearch}?type=Payable&menu=${isRc ? 'VisitsAsRc' : 'VisitsAsRfr'}`)"
            >
              <span class="inspections-submenu-page__card-title"
                >EFR Financial Report History</span
              >
              <span class="inspections-submenu-page__card-description"
                >Review Submitted EFR Financial Reports</span
              >
            </div>
            <div
              v-if="isRc"
              class="inspections-submenu-page__card"
              @click="redirect(`${Pages.VisitsOverview}`)"
            >
              <span class="inspections-submenu-page__card-title"
                >Visits Overview</span
              >
              <span class="inspections-submenu-page__card-description">
                Visual Overview of Submitted Inspection Reports
              </span>
            </div>
            <div
              v-if="isRc"
              class="inspections-submenu-page__card"
              @click="redirect('https://ou.jotform.com/250845100297050', true)"
            >
              <span class="inspections-submenu-page__card-title"
                >RC Travel Authorization Request</span
              >
              <span class="inspections-submenu-page__card-description">
                Submit Travel Authorization Request
              </span>
            </div>
            <div
              v-if="isRc && isGroupLeader"
              class="inspections-submenu-page__card"
              @click="
                redirect(
                  'https://ou.jotform.com/inbox/250845100297050/pendingApprovals',
                  true
                )
              "
            >
              <span class="inspections-submenu-page__card-title">
                Travel Requests Review / Approval</span
              >
              <span class="inspections-submenu-page__card-description">
                Review and Approve Pending Travel Authorization Requests
              </span>
            </div>
          </div>
        </div>
        <GlobalFooter />
      </template>
    </MasterPage>
  </div>
</template>

<script setup lang="ts">
import MasterPage from "@/views/MasterPage.vue";
import GlobalFooter from "@/views/GlobalFooter.vue";
import AccessHandler from "@/shared/access/AccessHandler.vue";
import { type RoleGroup, RfrVisitsGroup } from "@/shared/access/RoleGroups";
import { computed, ref } from "vue";
import { SubApplicationTypes } from "@/Domain/SubApplicationTypes";
import { UserRoles } from "@/Domain/UserRoles";
import { useMainStore } from "@/stores/useMainStore";
import { Pages } from "@/pages/pages";

const main = useMainStore();

const roleGroups = [RfrVisitsGroup] as RoleGroup[];

const urlParams = new URLSearchParams(window.location.search);
const visitsForRc = urlParams.get("VisitsForRole") == "RC";

const hasAccess = ref<boolean>(false);

const isGroupLeader = computed(() => {
  return main.isInRole(UserRoles.ROLE_GROUP_LEADER);
});

const isRc = computed(() => {
  return (
    visitsForRc &&
    (main.isInRole(UserRoles.ROLE_RABBINIC_COORDINATOR) || isGroupLeader.value)
  );
});

const redirect = function (url: string, newTab: boolean = false) {
  if (newTab) {
    window.open(url, "_blank");
    return;
  }
  window.location.href = url;
};
</script>

<style lang="scss" scoped>
.inspections-submenu-page {
  &__content-container {
    margin: 10px 30px 30px;
    padding: 0;
    width: auto;
  }

  &__notice {
    font-size: 20px;
    font-weight: bold;
    color: red;
    margin-bottom: 20px;
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    max-width: 800px;
    margin: 0 -10px;
  }

  &__card-title {
    display: block;
    font-size: 15px;
    background: #565656;
    padding: 10px;
    color: white;
    letter-spacing: 1px;
    transition: all 0.2s ease-in-out;
  }

  &__card {
    background: white;
    border-radius: 4px;
    box-shadow: 0 0 10px rgb(0 0 0 / 10%);
    color: #333;
    width: 48%;
    margin: 0 1% 20px;
    text-decoration: none;
    position: relative;
    top: 0;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    &:hover {
      top: -5px;
      box-shadow: 2px 2px 15px rgb(0 0 0 / 20%);

      .inspections-submenu-page__card-title {
        background: #3a5da6;
      }
    }
  }

  &__card-description {
    padding: 10px;
    display: block;
    color: #666;
  }
}
</style>
